import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ulid } from 'ulid';

import {
  INCLUDE_ALL_OF,
  MULTI_SELECT_FILTER_MODIFIERS,
  type MultiSelectFilterGroup,
  type MultiSelectFilterModifier,
  type MultiSelectFilterValue,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { MultiSelectFilterRow } from './multi_select_filter_row';
import type {
  MultiSelectFilterFieldProps,
  MultiSelectFilterItemDisplay,
} from './types';

import styles from './multi_select_filter_field.module.css';

type RowWithId = {
  id: string;
  group: MultiSelectFilterGroup;
};

const DEFAULT_MODIFIER = INCLUDE_ALL_OF;

function updateRowGroup(
  rows: RowWithId[],
  targetIndex: number,
  groupUpdate: Partial<MultiSelectFilterGroup>,
): RowWithId[] {
  return rows.map((row, index) =>
    index === targetIndex
      ? { ...row, group: { ...row.group, ...groupUpdate } }
      : row,
  );
}

export function MultiSelectFilterField({
  value,
  onChange,
  loadOptions,
  resolveSelectedItems,
  itemLabels,
  renderItemChip,
  availableModifiers = MULTI_SELECT_FILTER_MODIFIERS,
  maxRows,
  disabled,
  showItemAvatar,
  showStartTypingHint,
  startTypingHint,
}: MultiSelectFilterFieldProps) {
  const defaultGroup = useMemo<MultiSelectFilterValue>(
    () => [{ modifier: availableModifiers[0] ?? DEFAULT_MODIFIER, items: [] }],
    [availableModifiers[0]],
  );

  const assignRowIds = useCallback(
    (groups: MultiSelectFilterValue): RowWithId[] =>
      groups.map((group) => ({ id: ulid(), group })),
    [],
  );

  const externalGroups = value?.length ? value : defaultGroup;

  const [rows, setRows] = useState<RowWithId[]>(() =>
    assignRowIds(externalGroups),
  );

  const previousExternalRef = useRef(externalGroups);
  useEffect(() => {
    if (previousExternalRef.current !== externalGroups) {
      previousExternalRef.current = externalGroups;
      setRows((previousRows) => {
        const isOwnUpdate =
          previousRows.length === externalGroups.length &&
          externalGroups.every((group, index) => {
            const prev = previousRows[index]?.group;
            return (
              prev === group ||
              (prev !== undefined &&
                prev.modifier === group.modifier &&
                prev.items === group.items)
            );
          });
        if (isOwnUpdate) {
          return previousRows;
        }
        return assignRowIds(externalGroups);
      });
    }
  }, [externalGroups, assignRowIds]);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(() => {
    const currentRows = rowsRef.current;
    const invalidRows = currentRows.filter(
      (row) => !availableModifiers.includes(row.group.modifier),
    );
    if (invalidRows.length === 0) {
      return;
    }
    const coercedRows = currentRows.map((row) => {
      if (availableModifiers.includes(row.group.modifier)) {
        return row;
      }
      const prefix = row.group.modifier.startsWith('include')
        ? 'include'
        : 'exclude';
      const fallback =
        availableModifiers.find((modifier) => modifier.startsWith(prefix)) ??
        availableModifiers[0];
      return { ...row, group: { ...row.group, modifier: fallback } };
    });
    setRows(coercedRows);
    const nonEmpty = coercedRows.filter((row) => row.group.items.length > 0);
    if (nonEmpty.length > 0) {
      onChangeRef.current?.(nonEmpty.map((row) => row.group));
    }
  }, [availableModifiers]);

  const [resolvedItems, setResolvedItems] = useState<
    Map<string, MultiSelectFilterItemDisplay>
  >(new Map());
  const resolvedItemsRef = useRef(resolvedItems);
  resolvedItemsRef.current = resolvedItems;
  const pendingResolutions = useRef<Set<string>>(new Set());

  /**
   * Resolves display info for item IDs not yet in the cache.
   * Uses a pending set to avoid duplicate in-flight requests for the same IDs.
   */
  const resolveUnknownItems = useCallback(
    async (itemIds: string[]) => {
      const unknownIds = itemIds.filter(
        (id) =>
          !resolvedItemsRef.current.has(id) &&
          !pendingResolutions.current.has(id),
      );
      if (unknownIds.length === 0) {
        return;
      }

      for (const id of unknownIds) {
        pendingResolutions.current.add(id);
      }

      try {
        const resolved = await resolveSelectedItems(unknownIds);
        setResolvedItems((previous) => {
          const next = new Map(previous);
          for (const item of resolved) {
            next.set(item.id, item);
          }
          return next;
        });
      } finally {
        for (const id of unknownIds) {
          pendingResolutions.current.delete(id);
        }
      }
    },
    [resolveSelectedItems],
  );

  useEffect(() => {
    const allItemIds = externalGroups.flatMap((group) => group.items);
    if (allItemIds.length > 0) {
      resolveUnknownItems(allItemIds);
    }
  }, [externalGroups, resolveUnknownItems]);

  const [preloadedOptions, setPreloadedOptions] = useState<
    MultiSelectFilterItemDisplay[] | undefined
  >();
  const [isPreloading, setIsPreloading] = useState(true);

  useEffect(() => {
    setIsPreloading(true);
    loadOptions('')
      .then((result) => {
        setPreloadedOptions(result);
      })
      .catch(() => {
        setPreloadedOptions(undefined);
      })
      .finally(() => {
        setIsPreloading(false);
      });
  }, [loadOptions]);

  const emitChange = (updatedRows: RowWithId[]) => {
    const nonEmptyRows = updatedRows.filter(
      (row) => row.group.items.length > 0,
    );
    if (nonEmptyRows.length > 0) {
      setRows(nonEmptyRows);
      onChange?.(nonEmptyRows.map((row) => row.group));
    } else {
      setRows(assignRowIds(defaultGroup));
      onChange?.([]);
    }
  };

  const handleModifierChange = (
    rowIndex: number,
    modifier: MultiSelectFilterModifier,
  ) => {
    const targetRow = rows.at(rowIndex);
    if (!targetRow || targetRow.group.items.length === 0) {
      return;
    }
    emitChange(updateRowGroup(rows, rowIndex, { modifier }));
  };

  const handleItemsChange = (rowIndex: number, items: string[]) => {
    emitChange(updateRowGroup(rows, rowIndex, { items }));
  };

  const handleClearRow = (rowIndex: number) => {
    const targetRow = rows.at(rowIndex);
    const updated = rows.filter((_, index) => index !== rowIndex);

    if (!targetRow || targetRow.group.items.length === 0) {
      setRows(updated.length > 0 ? updated : assignRowIds(defaultGroup));
      return;
    }

    emitChange(updated);
  };

  const handleAddRow = () => {
    setRows((previous) => [
      ...previous,
      {
        id: ulid(),
        group: defaultGroup[0],
      },
    ]);
  };

  return (
    <div className={styles.root}>
      {rows.map((row, index) => (
        <MultiSelectFilterRow
          key={row.id}
          group={row.group}
          rowIndex={index}
          isFirstRow={index === 0}
          isLastRow={index === rows.length - 1}
          canAddRow={row.group.items.length > 0}
          showAddButton={maxRows === undefined || rows.length < maxRows}
          resolvedItems={resolvedItems}
          itemLabels={itemLabels}
          renderItemChip={renderItemChip}
          availableModifiers={availableModifiers}
          disabled={disabled}
          showItemAvatar={showItemAvatar}
          showStartTypingHint={showStartTypingHint}
          startTypingHint={startTypingHint}
          onModifierChange={handleModifierChange}
          onItemsChange={handleItemsChange}
          onClearRow={handleClearRow}
          onAddRow={handleAddRow}
          loadOptions={loadOptions}
          preloadedOptions={preloadedOptions}
          isPreloading={isPreloading}
        />
      ))}
    </div>
  );
}
